import { FC, useState, useEffect } from 'react';
import {
	Box,
	Collapse,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	Paper,
	Divider,
	Alert,
	useTheme,
	Fade,
} from '@mui/material';
import { CalendarMonth, Schedule, DirectionsCar, EventAvailable, ErrorOutline } from '@mui/icons-material';
import DayPicker from '@acorn-portal/shared/src/components/datePicker/day-picker';
import { GetMtaStartDateRange, getDateOnly } from '@acorn-portal/shared/src/pages/mta/utils';
import { add, formatISO } from 'date-fns';
import { useFutureDateTime } from '@acorn-portal/shared/src/hooks/useFutureTimeSelection';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useNavigate } from 'react-router';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	setStartDate,
	setIsMtaFutureDate,
	setPolicyReference,
	setPolicyToken,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setNextValidFutureDateTime } from '../../utils/formDateValueUtils';

// Validation schema
const dateValidationSchema = Yup.object().shape({
	dateSelected: Yup.boolean().required().oneOf([true], 'Please select a valid date and time'),
});

interface DateFormValues {
	dateSelected: boolean;
}

const TopUpMilesDate: FC = () => {
	const [futureDatePicker, setFutureDatePicker] = useState(false);
	const [min, max] = GetMtaStartDateRange();
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const policy = useAppSelector((state) => state.fullPolicy);

	useEffect(() => {
		dispatch(setPolicyReference(policy.policyNumber));
		dispatch(setPolicyToken(policy.policyToken));
	}, [dispatch, policy.policyNumber, policy.policyToken]);

	const initialDate = new Date(); // Default to current date
	const [formSubmitted, setFormSubmitted] = useState(false);

	const { hour, setHour, minute, setMinute, error, errorMessage, mtaStartDate, setMtaStartDate, mtaDateTime } =
		useFutureDateTime(initialDate, futureDatePicker);

	const setDefaultAdjustmentStartDate = () => {
		const now = new Date();
		const fifteenMinutesFromNow = add(now, { minutes: 15 });
		return fifteenMinutesFromNow;
	};

	const handleFutureDateToggleChange = (_event: React.MouseEvent<HTMLElement>, isFutureDatePickerSelected: boolean) => {
		if (!isFutureDatePickerSelected) {
			setDefaultAdjustmentStartDate();
		}
	};

	const initialValues: DateFormValues = {
		dateSelected: !error,
	};

	const handleSubmit = () => {
		setFormSubmitted(true);

		if (error) {
			return;
		}
		// Save date information to the store
		if (futureDatePicker) {
			const isoMTADateTime = formatISO(mtaDateTime);
			dispatch(setStartDate(isoMTADateTime));
			dispatch(setIsMtaFutureDate(true));
		} else {
			const defaultDate = setDefaultAdjustmentStartDate();
			const isoDefaultDate = formatISO(defaultDate);

			dispatch(setStartDate(isoDefaultDate));
			dispatch(setIsMtaFutureDate(false));
		}

		navigate(ROUTE_PATHS.makeAChangeMileage);
	};

	const setMtaDate = (date: string) => {
		dispatch(setStartDate(date));
	};

	const updateMtaStartDateTime = () => {
		const { date, hour, minute } = setNextValidFutureDateTime(new Date(), 15);
		setHour(hour);
		setMinute(minute);
		setMtaStartDate(date);
		setMtaDate(formatISO(date));
	};

	return (
		<JourneyCardLayout pageTitle='Top Up Your Mileage' loading={false}>
			<Box maxWidth='md' mx='auto'>
				<Fade in timeout={600}>
					<Box sx={{ width: '100%', mb: 2 }}>
						<Paper
							elevation={0}
							sx={{
								p: 3,
								mb: 3,
								backgroundColor: theme.palette.primary.light + '10',
								borderRadius: 2,
								border: `1px solid ${theme.palette.primary.light}`,
							}}
						>
							<Box display='flex' alignItems='flex-start' mb={2}>
								<DirectionsCar
									color='primary'
									sx={{
										mr: 2,
										fontSize: '2rem',
										mt: 0.5,
									}}
								/>
								<Box>
									<Typography variant='h6' gutterBottom color='primary.main' fontWeight='500'>
										Before we top up your mileage
									</Typography>
									<Typography variant='body1'>
										You need to select when you want this change to take effect. This is important as it determines when
										your increased mileage allowance will begin.
									</Typography>
								</Box>
							</Box>
						</Paper>

						<Formik
							initialValues={initialValues}
							validationSchema={dateValidationSchema}
							onSubmit={handleSubmit}
							validateOnChange={false}
							validateOnBlur={false}
							enableReinitialize={true}
						>
							{({ errors, setFieldValue, handleSubmit }) => (
								<Form>
									<Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
										<EventAvailable sx={{ mr: 1.5, color: theme.palette.primary.main }} />
										Select an effective date
									</Typography>

									<Typography variant='body2' sx={{ mb: 3, ml: 0.5 }}>
										Please choose when you want your mileage change to begin:
									</Typography>

									<Box sx={{ textAlign: 'center', mb: 2 }}>
										<ToggleButtonGroup
											color='primary'
											value={futureDatePicker}
											exclusive
											onChange={handleFutureDateToggleChange}
											aria-label='Change Start Date Options'
											sx={{
												'& .MuiToggleButton-root': {
													px: 3,
													py: 1,
													borderRadius: '4px',
													'&.Mui-selected': {
														backgroundColor: theme.palette.primary.main,
														color: 'white',
														'&:hover': {
															backgroundColor: theme.palette.primary.dark,
														},
													},
												},
											}}
										>
											<ToggleButton
												value={false}
												onClick={() => {
													setFutureDatePicker(false);
													setFieldValue('dateSelected', true);
												}}
											>
												<Schedule sx={{ pr: 1 }} />
												Apply Now
											</ToggleButton>
											<ToggleButton
												value={true}
												onClick={() => {
													updateMtaStartDateTime();
													setFutureDatePicker(true);
													// Update Formik validation based on the current error state
													setFieldValue('dateSelected', !error);
												}}
											>
												<CalendarMonth sx={{ pr: 1 }} />
												Future Date
											</ToggleButton>
										</ToggleButtonGroup>
									</Box>

									<Collapse in={futureDatePicker}>
										<Typography variant='body1' fontWeight='500' sx={{ mb: 2, ml: { sm: 0, md: 6 } }}>
											Please select the day you want the mileage change to start from
										</Typography>

										<DayPicker
											maxDate={max}
											minDate={min}
											time
											minute={minute}
											hour={hour}
											setHour={setHour}
											setMinute={setMinute}
											error={error}
											errorMessage={errorMessage}
											value={mtaStartDate ?? initialDate}
											onChange={(date) => {
												if (date) {
													const dateOnly = getDateOnly(date);
													setMtaStartDate(dateOnly);
													// Update Formik values based on date selection
													setFieldValue('dateSelected', !error);
												}
											}}
										/>
										{!error && (
											<Alert severity='info' sx={{ mt: 2 }}>
												Your policy will be updated with the new mileage from the selected date and time.
											</Alert>
										)}
									</Collapse>

									{!futureDatePicker && (
										<Alert severity='success' icon={<Schedule color='success' />} sx={{ mt: 2, ml: { sm: 0, md: 6 } }}>
											Your mileage change will be applied as soon as you complete this process, typically within 15
											minutes.
										</Alert>
									)}

									{formSubmitted && errors.dateSelected && futureDatePicker && (
										<Alert severity='error' icon={<ErrorOutline />} sx={{ mt: 2, mb: 2 }}>
											{errors.dateSelected}
										</Alert>
									)}

									<Box sx={{ mt: 4, mb: 2 }}>
										<Divider sx={{ mb: 2 }} />
										<Typography variant='body2' color='text.secondary'>
											If you have any questions about changing your mileage, please contact our customer service team
											using the details in your policy documents.
										</Typography>
									</Box>

									<ActionButtons>
										<BackButton
											onClick={() => {
												const encodedPolicyNumber = encodeURIComponent(policy.policyNumber);
												navigate(
													`${ROUTE_PATHS.policyDetails}/${policy.policyToken}?policyNumber=${encodedPolicyNumber}`,
												);
											}}
										>
											Back
										</BackButton>
										<ContinueButton
											onClick={() => {
												setFormSubmitted(true);
												handleSubmit();
											}}
										>
											Continue
										</ContinueButton>
									</ActionButtons>
								</Form>
							)}
						</Formik>
					</Box>
				</Fade>
			</Box>
		</JourneyCardLayout>
	);
};

export default TopUpMilesDate;
